import FolderDAOCache from 'file-browser/FolderDAOCache';
import {EditOp} from 'molbio/biointelligent-links/replace-link/constants';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

import {AaSequenceServerResponse, AaSequenceVersion} from './util/AaSequenceConstants';

export interface AaSequenceCreateData {
  name: string;
  aminoAcids: string;
  folder: string;
  translationLink?: {
    containerSequenceId: string;
    tagSchemaFieldId: number;
  } | null;
  tagSchemaId?: string;
  studyIds?: Array<string>;
}

export interface ImportAaSequenceResponse {
  results: Array<AaSequenceServerResponse>;
  error: string | null;
  warningMessage: string | null;
}

export interface AaSequenceUpdateData {
  name?: string;
  folder_id?: string;
  tagSchemaId?: string;
}

interface AnnotationUpdateData {
  name: string;
  type?: string;
  start: number;
  end: number;
  color: string;
  notes?: string;
  expectedVersionId?: number;
}

const AaSequenceDAO = {
  create({
    name,
    aminoAcids,
    folder,
    translationLink,
    tagSchemaId,
    studyIds,
  }: AaSequenceCreateData): JQueryPromise<AaSequenceServerResponse> {
    return net
      .postJSON({
        url: '/1/api/aa-sequences/',
        data: {
          name,
          aminoAcids,
          folder,
          translationLink,
          tagSchemaId,
          studyIds,
        },
      })
      .done(() => {
        FolderDAOCache.invalidateFolder(folder);
      });
  },

  importProtein({
    location,
    folderId,
    tagSchemaId,
  }: {
    location: string;
    folderId: string;
    tagSchemaId?: string;
  }): JQueryPromise<ImportAaSequenceResponse> {
    return TaskRunner.runTask({
      url: '/1/api/aa-sequences/import',
      contentType: 'application/json',
      type: 'POST',
      dataType: 'json',
      data: JSON.stringify({
        location,
        destinationFolder: folderId,
        tagSchemaId,
      }),
    });
  },

  dbImportProtein({
    accession,
    database,
    folderId,
  }: {
    accession: string;
    database: string;
    folderId: string;
  }): JQueryPromise<{protein: AaSequenceServerResponse}> {
    return TaskRunner.runTask({
      url: '/1/api/aa-sequences/db-import',
      contentType: 'application/json',
      type: 'POST',
      dataType: 'json',
      data: JSON.stringify({accession, database, folderId}),
    });
  },

  get(proteinId: string, versionId?: number, view?: boolean): JQueryPromise<AaSequenceServerResponse> {
    let data: {view?: boolean; version?: number} = {view};
    if (versionId != null) {
      data = {
        version: versionId,
        ...data,
      };
    }
    return net.getJSON(`/1/api/aa-sequences/${proteinId}`, data);
  },

  update(
    proteinId: string,
    // eslint-disable-next-line camelcase
    {name, folder_id, tagSchemaId}: AaSequenceUpdateData,
    expectedVersionId: number
  ): JQueryPromise<AaSequenceServerResponse> {
    return net.patchJSON({
      url: `/1/api/aa-sequences/${proteinId}`,
      // eslint-disable-next-line camelcase
      data: {name, folder: folder_id, tagSchemaId, expectedVersionId},
    });
  },

  delete(proteinId: string, expectedVersionId: number): JQueryPromise<{}> {
    return net.delete_({
      url: `/1/api/aa-sequences/${proteinId}`,
      data: {expectedVersionId},
    });
  },

  revert(
    proteinId: string,
    versionId: number,
    expectedVersionId: number
  ): JQueryPromise<AaSequenceServerResponse> {
    return net.postJSON({
      url: `/1/api/aa-sequences/${proteinId}/actions/revert-version`,
      data: {version: versionId, expectedVersionId},
    });
  },

  fetchVersions(
    proteinId: string
  ): JQueryPromise<{data: Array<AaSequenceVersion>; versionsWereTruncated?: boolean}> {
    return net.getJSON(`/1/api/aa-sequences/${proteinId}/versions`);
  },

  insertAminoAcids(
    proteinId: string,
    {
      position,
      aminoAcids,
      expectedVersionId,
    }: {position: number; aminoAcids: string; expectedVersionId: number}
  ): JQueryPromise<AaSequenceServerResponse> {
    return net.postJSON({
      url: `/1/api/aa-sequences/${proteinId}/actions/insert-amino-acids`,
      data: {position, aminoAcids, expectedVersionId},
    });
  },

  deleteAminoAcids(
    proteinId: string,
    {start, end, expectedVersionId}: {start: number; end: number; expectedVersionId: number}
  ): JQueryPromise<AaSequenceServerResponse> {
    return net.postJSON({
      url: `/1/api/aa-sequences/${proteinId}/actions/delete-amino-acids`,
      data: {start, end, expectedVersionId},
    });
  },

  replaceAminoAcids(
    proteinId: string,
    {
      start,
      end,
      aminoAcids,
      expectedVersionId,
    }: {start: number; end: number; aminoAcids: string; expectedVersionId: number}
  ): JQueryPromise<AaSequenceServerResponse> {
    return net.postJSON({
      url: `/1/api/aa-sequences/${proteinId}/actions/replace-amino-acids`,
      data: {start, end, aminoAcids, expectedVersionId},
    });
  },

  createAnnotation(
    proteinId: string,
    name: string,
    type: string | null,
    notes: string | null,
    start: number,
    end: number,
    color: string,
    expectedVersionId: number
  ): JQueryPromise<AaSequenceServerResponse> {
    return net.postJSON({
      url: `/1/api/aa-sequences/${proteinId}/annotations/`,
      data: {name, type, notes, start, end, color, expectedVersionId},
    });
  },

  updateAnnotation(
    proteinId: string,
    annotationId: string,
    {name, type, notes, start, end, color, expectedVersionId}: AnnotationUpdateData
  ): JQueryPromise<AaSequenceServerResponse> {
    return net.patchJSON({
      url: `/1/api/aa-sequences/${proteinId}/annotations/${annotationId}`,
      data: {name, type, notes, start, end, color, expectedVersionId},
    });
  },

  deleteAnnotation(
    proteinId: string,
    annotationId: string,
    expectedVersionId: number
  ): JQueryPromise<AaSequenceServerResponse> {
    return net.delete_({
      url: `/1/api/aa-sequences/${proteinId}/annotations/${annotationId}`,
      data: {expectedVersionId},
    });
  },

  autoAnnotate(
    proteinId: string,
    expectedVersionId: number,
    featureLibraryIds: Array<number>
  ): JQueryPromise<AaSequenceServerResponse> {
    return net.postJSON({
      url: `/1/api/aa-sequences/${proteinId}/actions/auto-annotate`,
      data: {expectedVersionId, featureLibraryIds},
    });
  },

  reverseTranslateSelection(aaSelection: string): Promise<{bases: string}> {
    return net.postJSONPromise({
      url: '/1/api/aa-sequences/reverse_translate',
      data: {aaSelection},
    });
  },

  bulkAutoAnnotate(aaSequenceIds: Array<string>, featureLibraryIds: Array<number>): Promise<void> {
    return Promise.resolve(
      TaskRunner.postJSON({
        url: '/1/api/aa-sequences:bulk-auto-annotate',
        // `proteinIds` is the server's request key; see benchling/molbio/aa_sequences/api/annotations.py
        data: {proteinIds: aaSequenceIds, featureLibraryIds},
      })
    );
  },

  bulkReimportAaSequences(
    files: Array<{name: string; s3Location: string}>,
    folderId: string
  ): Promise<{sequenceIds: Array<string>}> {
    return TaskRunner.postJSONPromise({
      url: '/1/api/aa-sequences:bulk-reimport',
      data: {files, folderId},
    });
  },

  // `proteinIds` is the server's request and response key; see benchling/molbio/aa_sequences/api/annotations.py
  bulkRemoveAnnotations(aaSequenceIds: Array<string>): Promise<{proteinIds: Array<string>}> {
    return net.postJSONPromise({
      url: '/1/api/aa-sequences/actions/bulk-remove-annotations',
      data: {proteinIds: aaSequenceIds},
    });
  },

  diffProteins(
    aaSequenceAId: string,
    aaSequenceBId: string
  ): Promise<{
    ops: Array<EditOp>;
    alignedA: string;
    alignedB: string;
  }> {
    return net.postJSONPromise({
      url: '/1/api/aa-sequences:generate-diff',
      // `proteinAId`/`proteinBId` are the server's request keys; see benchling/molbio/aa_sequences/api/aa_sequences.py
      data: {proteinAId: aaSequenceAId, proteinBId: aaSequenceBId},
    });
  },
};

export default AaSequenceDAO;
